import React, { PropTypes, PureComponent } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Promise from 'bluebird';
import { createAuthenticator } from 'common/actions/authenticationActions';
import { AuthenticatorType } from 'enums';
import { queryString } from 'lib/format';

function sendMessage(data) {
    if (!window.opener) {
        return;
    }
    const origin = `${window.location.protocol}//${window.location.host}`;
    window.opener.postMessage(JSON.stringify(data), origin);
}

export function getRedirectUrl(query = {}, redirectUrl, syndicated) {
    const { id, network } = query;
    if (!redirectUrl && !syndicated) {
        // If the `id` or `network` query parameters are defined, go directly to the /player route to play the asset via `id`
        if (id || network) {
            return `http://${window.location.host}/watch/player${queryString(query)}`;
        }
        // NOTE: We might not have an `id` coming from an 3rd party provider.
        // In this case, we want to redirect users to the main /watch homepage
        // SEE: https://espnjira.disney.com/browse/WEWEB-2333
        return `http://${window.location.host}/watch${queryString(query)}`;
    }

    return redirectUrl;
}

export class AdobePassProxyLoginComponent extends PureComponent {
    componentDidMount() {
        window.addEventListener('message', this._onPostMessage);

        try {
            window.localStorage.setItem(
                AdobePassProxyLoginComponent.XD_LOCALSTORAGE_KEY,
                AdobePassProxyLoginComponent.XD_LOCALSTORAGE_VAL
            );
        } catch (err) {
            console.error(err);
        }

        sendMessage({
            type: AdobePassProxyLoginComponent.EVENT_LOADED
        });

        const { dispatch, location: { query: {
            channel,
            id,
            mvpd,
            network: networkQuery,
            redirectUrl: redirectUrlQuery,
            syndicated
        } } } = this.props;

        const network = networkQuery || channel;
        const redirectUrl = getRedirectUrl({ id, network }, redirectUrlQuery, syndicated);
        const authenticator = dispatch(createAuthenticator(AuthenticatorType.ADOBE));

        authenticator.initialize()
        .then(() => authenticator.authenticate())
        .then((isAuthenticated) => {
            if (!isAuthenticated) {
                return Promise.resolve({});
            }
            return authenticator.getAuthenticationInfo();
        })
        .then(({ mvpd: currentMvpd }) => {
            // User is not authenticated, initialize the Auth flow for the provider via `mvpd`
            if (!currentMvpd) {
                return authenticator.selectProvider({
                    ID: mvpd
                });
            }

            // Attempting to auth with a different provider than currently authd as
            if (currentMvpd !== mvpd) {
                return authenticator.deauthenticate()
                    .then(() => {
                        window.location.href = window.location.href;
                    });
            }

            // if a redirectUrl exists, this indicates we are being linked to by an external site
            if (redirectUrl && !syndicated) {
                window.location.href = redirectUrl;
                return null;
            }

            // user is authd successfully for syndicated player.  message parent window and close self
            let entitlementToken;
            try {
                entitlementToken = window.localStorage.getItem('entitlementToken');
            } catch (err) {
                console.error(err);
            }

            sendMessage({
                type: AdobePassProxyLoginComponent.EVENT_AUTHENTICATE,
                entitlementToken,
                status: 'success'
            });

            window.close();
            return null;
        });
    }

    _onPostMessage(event) { // eslint-disable-line class-methods-use-this
        if (typeof event.data !== 'string') {
            return;
        }

        let data;
        try {
            data = JSON.parse(event.data);
        } catch (err) {
            data = {};
        }

        const { type, params } = data;
        if (type === AdobePassProxyLoginComponent.EVENT_CLOSE_REQUEST) {
            sendMessage({
                type: AdobePassProxyLoginComponent.EVENT_CLOSED,
                params
            });
            window.close();
        }
    }

    render() {
        return (
            <Helmet
                meta={[
                    { name: 'robots', content: 'noindex, nofollow' }
                ]}
            />
        );
    }
}

AdobePassProxyLoginComponent.propTypes = {
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.object.isRequired
};

AdobePassProxyLoginComponent.XD_LOCALSTORAGE_KEY = '@watch/XD';
AdobePassProxyLoginComponent.XD_LOCALSTORAGE_VAL = 'xdomain';
AdobePassProxyLoginComponent.EVENT_LOADED = 'watchespn:adobepass-login:loaded';
AdobePassProxyLoginComponent.EVENT_AUTHENTICATE = 'watchespn:adobepass-login:authentication';
AdobePassProxyLoginComponent.EVENT_CLOSED = 'watchespn:adobepass-login:closed';
AdobePassProxyLoginComponent.EVENT_CLOSE_REQUEST = 'watchespn:adobepass-login:closeRequest';

export default connect()(AdobePassProxyLoginComponent);



// WEBPACK FOOTER //
// ./app/common/page/AdobePassProxyLogin.js